import React from "react";
import styled from "styled-components";
import { SpaceProps, space, FlexboxProps, flexbox } from "styled-system";

import ConstellationSVG from "./ConstellationSVG";
import Box from "../Box/Box";
import Typography from "../Typography/Typography";
import Link from "../Link/Link";
import ResponsiveLayout from "../ResponsiveLayout/ResponsiveLayout";
import CAGLogo from "./white-cag-logo.png";
import { buildLegalInfo } from "../LegalInfoBuilder";
import useFetch from "react-fetch-hook";

export interface CAGFooterProps {
	/**
  - urls object has the following props
  - legalInfo: string; // Url to get the legal info data
 */
	urls: {
		legalInfo: string;
	};
}
const Footer = styled(Box)`
	overflow: hidden;
	box-sizing: border-box;
	position: relative;
`;

const StyledConstellationSVG = styled(ConstellationSVG)`
  position: absolute;
  right: -180px;
  @media only screen and (max-width: 1024px) {
    right: calc(0.8 * -180px);
    svg:first-of-type {
      width: calc(0.8 * 607px);
      height: calc(0.8 * 193px);
    };

    @media only screen and (max-width: 860px) {
      right: calc(0.65 * -180px);
      svg:first-of-type {
        width: calc(0.65 * 607px);
        height: calc(0.65 * 193px);
      }
  }
`;

const StyledLink = styled(Link)<SpaceProps>`
	${space}
`;

const defaultData = {
	"@name": "Footer-Legal",
	"@path": "/CAG/Footer-Legal",
	"@id": "9b143985-124d-4af4-bf22-e859ebf5d3e4",
	"@nodeType": "mgnl:folder",
	name: "Footer-Legal",
	"Terms-of-Use": {
		"@name": "Terms-of-Use",
		"@path": "/CAG/Footer-Legal/Terms-of-Use",
		"@id": "4b89eb7f-03e7-401f-a134-1835aeadc96f",
		"@nodeType": "mgnl:menuLink",
		label: "Terms of Use",
		name: "Terms of Use",
		isCategory: false,
		brandName: "BCAUK",
		link: "https://www.constellationautomotive.com/website-terms-of-use",
		isVisible: true,
		openInNewWindow: false,
		"@nodes": [],
	},
	"Privacy-Policy": {
		"@name": "Privacy-Policy",
		"@path": "/CAG/Footer-Legal/Privacy-Policy",
		"@id": "3bcdf28f-fd54-4472-80a2-978fa75467ea",
		"@nodeType": "mgnl:menuLink",
		label: "Privacy Policy",
		name: "Privacy Policy",
		isCategory: false,
		brandName: "BCAUK",
		link: "https://www.constellationautomotive.com/website-privacy-policy",
		isVisible: true,
		openInNewWindow: false,
		"@nodes": [],
	},
	"@nodes": ["Terms-of-Use", "Privacy-Policy"],
};

const LegalInfoContainer = styled(Typography)<FlexboxProps>`
	${flexbox}
	list-style: none;
	padding: 0;
	align-items: center;
`;

const LegalItem = styled.li`
	display: flex;
`;

const Separator = () => (
	<Typography
		display={{ _: "none", tablet: "block" }}
		variant="body"
		htmlTag="span"
		color="cag10"
		mx={3}
		css={`
			user-select: none;
		`}
	>
		•
	</Typography>
);

const CompanyDetails = ({ urls: { legalInfo } }: CAGFooterProps) => {
	const { data, error } = useFetch(legalInfo);
	const legalData = error ? defaultData : data;
	const legalItems = buildLegalInfo(legalData);

	const copyrightYear = new Date().getFullYear();

	return (
		<Box
			mt={{ _: 6, desktop: "44px" }}
			as="section"
			aria-label="Company details"
		>
			<Box display="flex" flexDirection="column" overflow="hidden">
				<Typography
					variant="body"
					color="cag10"
					m={0}
					textAlign={{ _: "center", desktop: "left" }}
					aria-label="copyright info"
				>
					© {copyrightYear} Constellation Automotive Group Limited, All Rights
					Reserved.
				</Typography>
				<LegalInfoContainer
					htmlTag="ul"
					variant="body"
					color="cag10"
					display="flex"
					mt={{ _: 5, tablet: 8, desktop: 3 }}
					mb={{ _: 5, tablet: 8, desktop: 3 }}
					textAlign={{ _: "center", desktop: "left" }}
					justifyContent={{
						_: "center",
						tablet: "center",
						desktop: "flex-start",
					}}
					flexDirection={{ _: "column", tablet: "row" }}
					flexWrap="wrap"
				>
					{legalItems
						.filter((item) => item.isVisible)
						.map((item: any, index: number) => {
							const isLast = index === legalItems.length - 1;

							return (
								<LegalItem key={item.id}>
									{index !== 0 && <Separator />}
									<StyledLink
										href={item.link}
										target={item.openInNewWindow ? "_blank" : "_top"}
										mb={isLast ? 0 : { _: 5, tablet: 0 }}
									>
										{item.label}
									</StyledLink>
								</LegalItem>
							);
						})}
				</LegalInfoContainer>
			</Box>

			<Typography
				variant="body"
				color="cag10"
				m={0}
				textAlign={{ _: "center", desktop: "left" }}
				aria-label="legal info"
			>
				Constellation Automotive Group Limited. Registered in England & Wales
				No. 12042162
				<br />
				Registered Office: Form 2, 18 Bartley Wood Business Park, Bartley Way,
				Hook, Hampshire, RG27 9XA, United Kingdom
			</Typography>
		</Box>
	);
};

const ConstellationFooter = (props: CAGFooterProps) => {
	return (
		<Footer
			data-testid="footer-container"
			height="auto"
			width="100%"
			display="flex"
			flex-direction="row"
			align-items="center"
			justifyContent={{ _: "center", desktop: "flex-start" }}
			pt={{ _: 56, desktop: "40px" }}
			pb={{ _: 56, desktop: 9 }}
			backgroundColor="cag9"
			as="footer"
		>
			<ResponsiveLayout>
				<Box
					display="flex"
					flexDirection="column"
					alignItems={{ _: "center", desktop: "flex-start" }}
					justifyContent={{ _: "center", desktop: "flex-start" }}
					height="100%"
					width="100%"
					role="figure"
				>
					<img
						src={CAGLogo}
						alt="Constellation Automotive Group"
						width="135px"
						height="43px"
					/>
					<CompanyDetails {...props} />
				</Box>
			</ResponsiveLayout>
			<StyledConstellationSVG display={{ _: "none", desktop: "block" }} />
		</Footer>
	);
};

export default ConstellationFooter;
